<template lang="pug">
  .qty(:class="theme" @click.stop)
    .btn(:class="{disabled: buyRequest.qty === 0 || cartItemDisableMin || disabled}" @click.stop="decrease")
      Icon(name="minus")
    .qty-field(@click="showMobile()" :class="{'mobile-input': inventory.min >= 5 || buyRequest.qty >= 5}")
      label.rogue-hidden-semantic-label(:for="fieldId")
        | {{ 'Quantity for ' + (products[buyRequest.productId] || {}).name || 'Product'  }}
      input(
        :value="buyRequest.qty"
        @click.stop
        @input="update($event.target.value || 0)"
        @blur="checkMinQty($event.target.value || 0)"
        type="tel"
        ref="input"
        :id="fieldId"
        :disabled="status === 'oos' || disabled")
      Tooltip.error-tip(:manual="true" ref="tt")
        .placeholder
        template(#tooltip)
          .error-text {{tooltip}}
      .mobile-opts(v-if="mobileOpts.length")
        .opt(v-for="opt in mobileOpts" :key="opt" :class="{selected: buyRequest.qty === opt}" @click.stop="update(opt)") {{opt}}
        .opt(@click.stop="focusInput()") 5+
    .btn(:class="{disabled: buyRequest.qty >= inventory.max || disabled}" @click.stop="increase")
      Icon(name="plus")
    bumper-drawer.bumper-set-breakdown(v-if="chooseSetBreakdown" :buyRequest="buyRequest")
</template>
<script setup lang="ts">
import { computed, defineAsyncComponent, onBeforeUnmount, ref, useTemplateRef, watch } from 'vue'
import { useListStore } from '@/js/stores/listStore'
import { useProductStore } from '@/js/stores/productStore'
import { useUIStore } from '@/js/stores/uiStore'
import { useRogue } from '@/js/services/Rogue'
import { needsBumperSetBreakdown } from '@/js/helpers/bumper-set'
import type { BuyRequest } from '@/js/services/RogueTypes'

const BumperDrawer = defineAsyncComponent(() => import(/* webpackChunkName: "bumper-set-breakdown" */'@/js/pages/product/components/bumper-set-breakdown/bumper-drawer.vue'))

const props = withDefaults(defineProps<{
  theme?: string,
  listId?: string,
  buyRequest: BuyRequest,
  disabled?: boolean,
  status?: string,
  hideDrawer?: boolean,
}>(), {
  disabled   : false,
  hideDrawer : false,
})

const rogue = useRogue()
const listStore = useListStore()
const productStore = useProductStore()
const uiStore = useUIStore()

const input = useTemplateRef<HTMLInputElement>('input')
const tt = useTemplateRef<{ hover: (val: boolean) => void }>('tt')

const mobileOpts = ref<number[]>([])
const tooltip = ref<string | null>(null)
const chooseSetBreakdown = ref(false)

const products = computed(() => productStore.products)
const lists = computed(() => listStore.lists)

const fieldId = computed(() => {
  const buyRequest = props.buyRequest as BuyRequest & { listId?: string }

  return `qty-${buyRequest._id || buyRequest.listId || buyRequest.productId}`
})

const simple = computed(() => {
  let buyRequest = props.buyRequest
  while (buyRequest.children?.length) {
    buyRequest = buyRequest.children.filter(c => c.qty > 0)[0] || buyRequest.children[0]
  }

  return buyRequest.productId
})

const product = computed(() => products.value[props.buyRequest.productId])

const inventory = computed(() => {
  const inv = {
    min : props.buyRequest._id || props.buyRequest.listItemId ? 1 : 0,
    max : 10000,
  }

  if (props.listId) {
    const list = lists.value[props.listId]
    if (list?.type === 'wishlist' || list?.type === 'registry') {
      // You can add as many qty as you want to your own registry or wishlist
      return inv
    }
  }

  const checkItem = (buyRequest: BuyRequest) => {
    const itemProduct = products.value[buyRequest.productId]
    if (itemProduct?.inventory) {
      const { type, inventory: { min, max, promoQty, ms } } = itemProduct
      if (itemProduct.isProgram) {
        inv.max = 1
      } else if (type === 'simple' || (type === 'configurable' && (min > 1 || max < 10000)) || ms !== false || promoQty) {
        if (min && min > 1) {
          inv.min = Math.max(inv.min, min)
        }
        if (max !== undefined) {
          inv.max = Math.min(inv.max, max)
        }
        if (promoQty) {
          inv.max = Math.min(promoQty, inv.max)
        }
      }
    }
    if (itemProduct?.type === 'bundle') {
      buyRequest.children?.filter((child, i) => i === 0 || child.qty > 0).forEach(child => checkItem(child))
    } else {
      buyRequest.children?.forEach(child => checkItem(child))
    }
  }

  checkItem(props.buyRequest)

  return inv
})

// In side-cart, cart and checkout, do not allow decrease `qty` below the inventory min value
const cartItemDisableMin = computed(() => props.theme === 'cart-item-qty' && props.buyRequest.qty <= inventory.value.min)

function closeMobile() {
  mobileOpts.value = []
}

function update(newVal: string | number) {
  if (props.disabled) {
    return
  }
  const parsed = parseInt(String(newVal), 10)
  if (Number.isNaN(parsed) || parsed < 0) {
    return
  }
  const val = Math.min(inventory.value.max, parsed)

  if (parsed > inventory.value.max) {
    tooltip.value = rogue.__('Max quantity')
    tt.value?.hover(true)
  } else {
    tooltip.value = ''
    tt.value?.hover(false)
  }

  // Used for opening bumper drawer for choosing set breakdowns
  chooseSetBreakdown.value = !props.hideDrawer && needsBumperSetBreakdown(product.value)

  if (chooseSetBreakdown.value) {
    uiStore.showOverlay(`bumper-set-breakdown-${props.buyRequest.productId}`, rogue)
  }

  mobileOpts.value = []
  window.removeEventListener('click', closeMobile)

  if (val !== props.buyRequest.qty) {
    const diff = val - props.buyRequest.qty
    props.buyRequest.qty = val

    // We dont want to update the list item with a quantit less than the min quantity...
    if ((props.buyRequest._id || props.buyRequest.listItemId) && props.listId && val >= inventory.value.min) {
      listStore.updateListItem(rogue, props.listId, props.buyRequest._id || props.buyRequest.listItemId, { qty: props.buyRequest.qty })

      if (diff > 0) {
        rogue.track.track({ type: 'addToCart', items: [{ ...props.buyRequest, qty: diff }] })
      } else if (diff < 0) {
        rogue.track.track({ type: 'removeFromCart', items: [{ ...props.buyRequest, qty: Math.abs(diff) }] })
      }
    }
  }
}

function decrease() {
  if (!cartItemDisableMin.value) {
    update(props.buyRequest.qty - 1)
  }
}

function increase() {
  update(props.buyRequest.qty + 1)
}

// If the min qty is 8, we can't actually fix it on every keystroke - because if I want 20, I have to enter 2 first
// checkMinQty is called on blur, which means they finished editing the qty field
function checkMinQty(newVal: string | number) {
  const parsed = parseInt(String(newVal), 10)
  if (Number.isNaN(parsed)) {
    update(0)
  } else if ((parsed !== 0 && inventory.value.min && parsed < inventory.value.min)) {
    update(inventory.value.min)
  }
}

// The purpose of this method is to set the correct `qty` when switching between children
function updateQty() {
  const value = Math.min(inventory.value.max, Math.max(inventory.value.min, props.buyRequest.qty))
  if (value !== props.buyRequest.qty) {
    props.buyRequest.qty = value
  }
}

function showMobile() {
  mobileOpts.value = []
  for (let i = inventory.value.min; i < Math.min(inventory.value.max, 5); i += 1) {
    mobileOpts.value.push(i)
  }
  if (mobileOpts.value.length) {
    window.addEventListener('click', closeMobile)
  }
}

function focusInput() {
  input.value?.focus()
  mobileOpts.value = []
}

watch(simple, () => {
  updateQty()
})

onBeforeUnmount(() => {
  window.removeEventListener('click', closeMobile)
})
</script>
<style lang="scss" scoped>
.qty {
  display: flex;
  align-items: stretch;
  user-select: none;
}

.btn, .qty-field input, .mobile-opts {
  border: 1px solid $color-gray;
  border-radius: $radius-sharp;
  flex-shrink: 0;
  height: 48px;
}

.btn {
  background-color: $color-white;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  cursor: pointer;

  &.disabled {
    opacity: $opacity-disabled;
  }

  @include not-mobile() {
    &:hover {
      border-width: 2px;
      border-color: $color-gray-dark;
    }
  }

  &:active {
    border-width: 2px;
    border-color: $color-black;
  }

  .icon {
    width: 12px;
    height: 12px;
  }
}

.qty-field {
  position: relative;
  pointer-events: none;
}

.qty-field input {
  padding: 0;
  text-align: center;
  width: 60px;
  line-height: 48px;
  flex-shrink: 0;
  font-size: $size-s;
  margin: 0 $space-xxs;
  pointer-events: all;
  color: $color-black;

  &[disabled] {
    color: $color-gray-dark;
    background-color: $color-gray-light;
  }
}

.mobile-opts {
  position: absolute;
  top: 0;
  left: $space-xxs;
  width: 60px;
  overflow: hidden;
  z-index: 1;
  height: auto;

  .opt {
    text-align: center;
    font-size: $size-s;
    width: 58px;
    line-height: 46px;
    background-color: $color-white;

    &.selected {
      background-color: $color-gray-light;
    }
  }
}

.error-tip {
  position: absolute;
  top: 0;
  left: 50%;

  .error-text {
    white-space: nowrap;
  }
}

.qty.group-child {
  @include mobile() {
    .btn {
      display: none;
    }
    .qty-field {
      pointer-events: all;
    }
    .qty-field:not(.mobile-input) input {
      pointer-events: none;
    }
  }
}

.qty.accessory {
  .btn {
    display: none;
  }
  .qty-field {
    pointer-events: all;
  }
  .qty-field:not(.mobile-input) input {
    pointer-events: none;
  }
}

</style>

<style lang="scss">
.bumper-set-breakdown .drawer-container .drawer-header h3 {
  margin-right: 40px;
}
</style>
